<script setup lang="ts">
import { useAddressSystemStore } from '@/store/addressSystem'
import { useBasketStore } from '@/store/basket/basket'
import { useBasketStateStore } from '@/store/basket/basket-state'
import { useDialogsStore } from '@/store/dialogs/dialogs'
import { useUserStore } from '@/store/user/user'

import { SetDeliveryMethodPayload } from '@/api/modules/basket/types'
import type { AddressWidgets } from '@/types/address-system'
import { BasketStep } from '@/types/basket'

import { storage } from '@/services/storage'
import { STORAGE_KEY } from '@/services/storage/constants'

const TITLE_NO_ADDRESS = 'Куда заказать?'
const TIMEOUT_MOBILE = 300
const TIMEOUT_DESKTOP = 0

const STATE_DELIVERY_WIDGET: AddressWidgets = {
  unset: {
    type: 'unset',
    icon: 'fill-location',
  },
  courier: {
    type: 'courier',
    icon: 'fill-truck-delivery',
  },
  hall: {
    type: 'hall',
    icon: 'fill-dish',
  },
  pickup: {
    type: 'pickup',
    icon: 'fill-shopping-bag',
  },
}

const addressSystemStore = useAddressSystemStore()
const basketStore = useBasketStore()
const basketStateStore = useBasketStateStore()
const userStore = useUserStore()
const dialogStore = useDialogsStore()

const { isMobile } = useResponsive()

// Флаг запуска тултипа
const isActiveTooltip = ref(false)

const isAuth = computed(() => userStore.isAuthenticated)

const isLoadingDeliveryWidget = computed(
  () => addressSystemStore.isLoadingWidget,
)

const currentDeliveryMethod = computed(() => {
  return isAuth.value
    ? basketStore.data.delivery.method
    : addressSystemStore.deliveryWidget.delivery_method
})

const currentBasketAddress = computed(() => {
  return isAuth.value
    ? basketStore.data.delivery.order_address
    : addressSystemStore.deliveryWidget.order_address
})

const formattedAddress = computed(() => {
  const address = currentBasketAddress.value?.split(', ') || []

  if (address.length < 2) return { street: address.join(), house: '' }

  const house = `,&nbsp;${address.pop()}`
  const street = address.join(', ')

  return { street, house }
})

// Состояние виджета
const currentDeliveryWidget = computed(() => {
  if (!isAuth.value && !addressSystemStore.deliveryWidget.order_address) {
    return STATE_DELIVERY_WIDGET.unset
  }

  return !currentDeliveryMethod.value || !currentBasketAddress.value
    ? STATE_DELIVERY_WIDGET.unset
    : STATE_DELIVERY_WIDGET[currentDeliveryMethod.value]
})

const isAddressUnset = computed(
  () => currentDeliveryWidget.value.type === 'unset',
)

const isBasketSecondStep = computed(() => {
  return basketStateStore.step === BasketStep.SECOND
})

const openDialogAddress = () => {
  if (isBasketSecondStep.value) return

  if (isMobile.value) isActiveTooltip.value = false

  addressSystemStore.setVisibleAddressSystemDialog(true)
}

const isVisibleTooltip = computed(
  () =>
    isActiveTooltip.value &&
    !currentBasketAddress.value &&
    !dialogStore.dialogs.login.visible,
)

// Устанавливаем начальное состояние виджета, в случае если пользователь не авторизован
const setInitialDeliveryBasket = () => {
  if (isAuth.value) return

  const deliveryWidget = storage.get<SetDeliveryMethodPayload>(
    STORAGE_KEY.addressSystem.deliveryWidget,
  )

  if (!deliveryWidget) return

  addressSystemStore.changeDeliveryBasket(deliveryWidget)
  addressSystemStore.changeDeliveryWidget(deliveryWidget)
}

const checkVisibleTooltip = () => {
  const deliveryWidget = storage.get<SetDeliveryMethodPayload>(
    STORAGE_KEY.addressSystem.deliveryWidget,
  )

  const isTurnOnTooltip = Boolean(
    !currentBasketAddress.value && isLoadingDeliveryWidget.value,
  )

  if (isAuth.value && isTurnOnTooltip) return

  if (!isAuth.value && deliveryWidget) return

  const timeOut = isMobile.value ? TIMEOUT_MOBILE : TIMEOUT_DESKTOP

  setTimeout(() => (isActiveTooltip.value = true), timeOut)
}

watch(
  () => currentBasketAddress.value,
  (newAddress) => {
    if (!newAddress) isActiveTooltip.value = true
  },
)

onMounted(() => {
  setInitialDeliveryBasket()

  checkVisibleTooltip()
})
</script>
<template>
  <div
    class="widget-delivery"
    :class="{ 'widget-delivery--hidden': isBasketSecondStep }"
    :aria-disabled="isBasketSecondStep"
    tabindex="0"
    @click="openDialogAddress"
    @keydown.enter="openDialogAddress"
    @keydown.space.prevent="openDialogAddress"
  >
    <the-tooltip
      :width="245"
      :offset="12"
      :active="isActiveTooltip"
      class="widget-delivery__tooltip"
      fixed
      pointer
      :pointer-offset="40"
      :deactivation-timeout="5000"
      :opacity="isMobile ? 1 : 0.84"
    >
      <template #activator>
        <the-loader
          v-if="isLoadingDeliveryWidget"
          color="var(--ui-color-primary)"
        />

        <div v-else class="widget-delivery__container">
          <div class="widget-delivery__type-delivery">
            <the-icon
              :name="currentDeliveryWidget.icon"
              color="var(--widget-icon-color)"
            />
          </div>

          <div
            :class="{ 'widget-delivery__address--not-address': isAddressUnset }"
            class="widget-delivery__address"
          >
            <template v-if="currentBasketAddress">
              <span
                class="widget-delivery__street"
                v-html="formattedAddress.street"
              />

              <span
                class="widget-delivery__house"
                v-html="formattedAddress.house"
              />
            </template>

            <template v-else>{{ TITLE_NO_ADDRESS }}</template>
          </div>

          <the-icon
            name="expand-right"
            color="var(--widget-arrow-color)"
            size="20"
          />
        </div>
      </template>

      <template v-if="isBasketSecondStep || isVisibleTooltip" #default>
        <span v-if="isBasketSecondStep" class="widget-delivery__message">
          Выбор адреса возможен только на&nbsp;первом этапе оформления заказа
        </span>

        <span v-if="isVisibleTooltip" class="widget-delivery__message">
          Укажите адрес, чтобы мы&nbsp;показали актуальное меню и&nbsp;условия
          доставки
        </span>
      </template>
    </the-tooltip>
  </div>
</template>

<style lang="scss" scoped>
.widget-delivery {
  --widget-icon-color: var(--ui-color-coral-500);
  --widget-arrow-color: var(--ui-color-sandy-700);
  --widget-text-color: var(--ui-color-sandy-950);

  width: 100%;
  height: 40px;
  cursor: pointer;
  background-color: var(--ui-color-sandy-50);
  border: 1px solid var(--ui-color-sandy-100);
  border-radius: 40px;
  transition: background-color 0.3s ease;

  &__container {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 7px 11px;
  }

  @include media('>=tablet') {
    width: 50%;
  }

  @include media('>=desktop-mini') {
    width: 335px;
    outline-offset: 3px;

    &:focus-visible {
      outline: 1px solid var(--ui-color-sandy-950);
    }

    &:hover:not(&--hidden) {
      background-color: var(--ui-color-sandy-100);
    }
  }

  &__type-delivery {
    width: 24px;
    height: 24px;
    margin-right: 8px;
  }

  &__address {
    display: flex;
    width: inherit;
    margin-right: 12px;
    overflow: hidden;
    font-size: 14px;
    font-weight: 600;
    line-height: 14px;
    color: var(--widget-text-color);
    white-space: nowrap;
    user-select: none;
    transition: color 0.3s ease;

    &--not-address {
      --widget-text-color: var(--ui-color-sandy-700);
    }
  }

  &__street {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__house {
    flex-shrink: 0;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__tooltip {
    display: block !important;
    height: 100%;
  }

  &__message {
    letter-spacing: -0.02em;
  }

  &--hidden {
    --widget-icon-color: var(--ui-color-sandy-400);
    --widget-arrow-color: var(--ui-color-sandy-400);
    --widget-text-color: var(--ui-color-sandy-400);

    cursor: default;
  }
}
</style>
